<template>
  <div class="dashboard">
    <div class="flex flex-row flex-nowrap items-center">
      <FilterBar
        class="flex grow items-center"
        v-show="!showSkeleton">
        <slot name="filter-bar-left" />
        <ModernColumnFilter
          :defaultFields="defaultFields"
          :appliedFields="appliedFields"
          :fields="columnPickerFields"
          @change="applyFields" />
        <ViewFilter
          :filters="filters"
          :appliedFilters="appliedFilters"
          :appliedOptions="appliedOptions"
          :showMoreOptions="true"
          :showFavoritesOption="allFilters"
          :filterOpen="filterOpen"
          :activeFilterCategory="activeFilterCategory"
          @filterClosed="
            filterOpen = false;
            activeFilterCategory = undefined;
          "
          @change="applyFilters" />

        <TableSortMenu
          v-if="consolidated"
          :options="consolidatedSortOptions"
          :model-value="selectedConsolidatedSort"
          :reversed="consolidatedSortReversed"
          @update:model-value="applyConsolidatedSort" />

        <MenuDropdown
          v-if="showSavedFiltersDropdown"
          :items="savedFilterMenuItems"
          :prependItems="savedFilterActionItems"
          bottom
          start
          size="sm">
          <template #action>
            <div
              role="button"
              tabindex="0"
              class="btn gap-1.5 border border-gray-300 btn-sm">
              <Bookmark class="h-4 w-4 stroke-primary" />
              My Saved Filters
            </div>
          </template>
        </MenuDropdown>

        <SaveCurrentFilterModal
          v-if="showSavedFiltersDropdown"
          :show="showSaveFilterModal"
          :filters="currentStoredFilters"
          :columns="appliedFields"
          @saved="onFilterSaved"
          @cancel="showSaveFilterModal = false" />

        <ManageSavedFiltersModal
          v-if="showSavedFiltersDropdown"
          :show="showManageFiltersModal"
          :filters="savedFilters"
          @updated="loadSavedFilters"
          @cancel="showManageFiltersModal = false" />

        <div class="spacer">&nbsp;</div>

        <Teleport
          v-if="!showSkeleton"
          defer
          to="#alternate-toggles"
          :disabled="!showTogglesOnTable">
          <div
            v-if="showFilterToggles"
            class="flex w-full justify-end gap-4 py-0.5 pr-3">
            <toggle
              v-if="showWithoutProgramToggle"
              id="toggle-without-program-only"
              label="Show Policies Without Program Only"
              v-model="showWithoutProgramOnly" />

            <toggle
              id="toggle-policies"
              :label="includeExternalPoliciesLabel()"
              v-model="showExternal" />

            <toggle
              id="toggle-current-only"
              label="Include Expired Policies"
              v-model="includeExpired" />
          </div>
        </Teleport>

        <slot name="filter-bar-right" />
      </FilterBar>

      <slot name="header-right" />
    </div>

    <TableSkeleton
      v-if="showSkeleton"
      :columns="skeletonColumns"
      :lines-per-row="consolidated ? 2 : 1" />

    <div
      id="policy-details-table-container"
      class="px-4 py-4"
      v-if="!showSkeleton">
      <slot name="inner" />
      <div
        id="policy-details-subheader"
        class="mb-1.5 flex content-start items-start gap-x-2">
        <div class="my-auto flex w-full items-center justify-between">
          <div class="flex items-center gap-2">
            <h2>{{ title }}</h2>
            <DownloadMenu
              start
              bottom
              :items="downloadMenuOptions"
              @export="handleDownloadExport" />
            <div class="flex items-center gap-1">
              <toggle
                id="toggle-include-coverage-lines"
                label="Include Coverage Lines in Download"
                v-model="includeCoverageLines" />
              <Tooltip
                text="Turning on this toggle will include coverage-line level detail for package policies."
                position="top">
                <Info
                  class="h-4 w-4 cursor-help stroke-gray-500"
                  :stroke-width="2" />
              </Tooltip>
            </div>
          </div>

          <div id="alternate-toggles" />

          <slot name="inner-append" />
          <div v-if="!isUserAClient && !clientViewEnabled && allowPolicyUpload" class="flex items-center gap-2">
            <Tooltip
              v-if="pendingBinderReviews.length > 0"
              :text="pendingBinderScanTooltip"
              :disabled="!pendingBinderScanTooltip">
              <AppButton
                outline
                warning
                :loading="!hasBinderScanReady"
                data-testid="binder-scan-pill"
                @click="openPendingBinderScan">
                {{ binderScanPillLabel }}
                <span
                  v-if="pendingBinderReviews.length > 1"
                  class="badge badge-sm badge-warning">
                  {{ binderScanPillCount }}
                </span>
              </AppButton>
            </Tooltip>
            <button
              class="btn btn-sm btn-primary"
              data-testid="external-policy-upload"
              @click="showUploadModal = true">
              Upload {{ externalPolicyName() }} Policies <Plus :size="18" />
            </button>
          </div>
        </div>
        <div
          v-if="isCreateNewProgram"
          class="w-90">
          <TextField
            v-model="searchValue"
            placeholder="Search"
            data-test="account-search"
            autocomplete="off"
            :appendIcon="searchIcon"
            @update:model-value="(v) => search(typeof v === 'string' ? v : String(v))" />
        </div>
      </div>

      <EmptyDisplay
        :src="emptySrc"
        alt="No policies"
        title="No policies found"
        v-if="!policies || policies.length === 0"
        text="The filters applied above are too restrictive. Please remove filters to view more policies." />

      <PolicyDetailTable
        v-if="policies && policies.length > 0"
        :auto-sort="!isPaged && !consolidated"
        :consolidated="consolidated"
        :consolidated-sort="consolidated ? { value: selectedConsolidatedSort, direction: activeConsolidatedSort.direction } : undefined"
        @consolidated-sort="applyConsolidatedSort"
        :visibleFields="displayedFields"
        :policies="allPolicies"
        :policy-hierarchy="policies"
        :perspective="scenario"
        @needs-update="refreshPolicies"
        :showChildren="showChildren"
        :sorts="sorts"
        @sort="sortByField"
        :extraColumnTitle="extraColumnTitle"
        :policy-document-types="policyDocumentTypes"
        @expand="expand"
        :height="policyDetailsTableHeight"
        :showWithoutProgram="showWithoutProgram"
        :policy-sources="policySources"
        :get-prepend-menu-items="getExtraMenuItems">
        <template
          v-slot:extraColumn="{ policy }"
          v-if="!!$slots.extraColumn">
          <slot
            name="extraColumn"
            v-bind="{ policy }" />
        </template>
        <template
          v-slot:extraColumnHeader
          v-if="!!$slots.extraColumnHeader">
          <slot name="extraColumnHeader" />
        </template>
        <template
          v-slot:actions="{ policy }"
          v-if="!!$slots.actions">
          <slot
            name="actions"
            v-bind="{ policy }" />
        </template>
      </PolicyDetailTable>

      <div
        id="policy-pagination-buttons"
        class="flex items-center justify-between gap-4 pt-4"
        v-if="!showSkeleton && (isPaged || hasPolicyTotals)">
        <div
          class="flex gap-4"
          v-if="isPaged">
          <AppButton
            secondary
            :disabled="!hasPrevPage"
            @click="previousPage"
            data-test="prev-button">
            <CircleChevronLeft
              :size="20"
              class="mr-2 text-primary" />
            Previous
          </AppButton>
          <AppButton
            secondary
            :disabled="!hasNextPage"
            @click="nextPage"
            data-test="next-button">
            Next
            <CircleChevronRight
              :size="20"
              class="ml-2 text-primary" />
          </AppButton>
        </div>
        <div
          v-if="hasPolicyTotals"
          class="ms-auto flex items-center gap-3 text-sm"
          data-test="policy-totals">
          <!-- The `{{ ' ' }}` spaces are load-bearing: Vue's whitespace condensing drops
               newline-only gaps between elements, so a formatter line-break here would
               otherwise render "4Policies". -->
          <span
            ><span class="font-medium">{{ displayedPolicyStats?.totalCount?.toLocaleString() }}</span
            >{{ ' ' }}<span class="text-base-content/60">Policies</span></span
          >
          <span class="text-base-content/30">|</span>
          <span
            ><span class="text-base-content/60">Total Premium</span>{{ ' '
            }}<span class="font-medium">{{ compactMoney(displayedPolicyStats?.totalPremium, undefined, 0, 2) }}</span></span
          >
        </div>
      </div>
    </div>

    <ExternalPolicyUploadModal
      :show="showUploadModal"
      :account-id="String(accountId)"
      :is-parent="isParent"
      @close="showUploadModal = false; refreshPendingBinderScans()"
      @uploaded="handleExternalPoliciesUploaded" />
  </div>
</template>

<script lang="ts">
import {
  USStates,
  shouldSkipPolicyFetch,
  sortPolicies,
  expandConsolidatedColumns,
  CONSOLIDATED_POLICY_COLUMNS,
  consolidatedContextColumns,
  CONSOLIDATED_SORT_OPTIONS,
  DEFAULT_CONSOLIDATED_SORT,
  parseNamedFilter,
  withExternalAgencyColumn,
  sortsWithoutExternalAgency,
  defaultPolicySorts,
  POLICY_TABLE_COLUMNS,
  type QueryParams,
} from './PolicyDetails';
import { resolveTableSort } from '@/utilities/TableSort';
import { PropType, markRaw } from 'vue';
import emptySrc from '@/assets/images/empty.svg';
import PolicyDetailTable from '@/components/PolicyDetailTable.vue';
import {
  accountService,
  carrierService,
  parentAccountService,
  PolicyAllParams,
  policyService,
  policySourceService,
  referenceService,
  wholesalerGroupService,
} from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { accountService as accountServiceV2 } from '@/services/v2/AccountService';
import { binderReviewService } from '@/services/v2/BinderReviewService';
import ExternalPolicyUploadModal from '@/components/v2/external_policy_upload/ExternalPolicyUploadModal.vue';
import { BinderReviewItem, PENDING_BINDER_REVIEW_STATUS } from '@/types/BinderReview';
import { DownloadCSV, TransformPoliciesToCSV } from '@/utilities/Download';
import { trackPolicyDetailsSort } from '@/utilities/Tracking';
import FilterBar from '@/components/v2/FilterBar.vue';
import ModernColumnFilter from '@/components/ModernColumnFilter.vue';
import ViewFilter from '@/components/ViewFilter.vue';
import { EpicPolicy } from '@/types/tower/Domain';
import {
  externalPolicySourceIDs,
  FlattenPolicies,
  GetPoliciesAsHierarchy,
  PoliciesAsHierarchy,
  PolicyStats,
  PolicyWithChildren,
} from '@/utilities/Policies';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import { compactMoney } from '@/utilities/Money';
import { calculatePPM } from '@/utilities/tower/Calculations';
import { LabelValue } from '@/types/label-value';
import { policyDetailsType } from '@/types/PolicyDetailsType';
import { DocumentType } from '@/types/v2/Document';
import { DateTime } from 'luxon';
import store from '@/store';
import { IsUserAClient, IsUserAnAdmin, IsUserABroker } from '@/utilities/AuthUtils';
import { externalPolicyName, includeExternalPoliciesLabel } from '@/utilities/OrgConfig';
import { PolicyDetailsFilter } from '@/types/PolicyDetailsFilter';
import { LocationQuery, RouteLocationRaw, useRoute } from 'vue-router';
import Toggle from '@/components/v2/Toggle.vue';
import TableSkeleton from '@/components/v2/TableSkeleton.vue';
import TextField from '@/components/v2/TextField.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { LabelFilterType } from '@/types/components/FilterType';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import DownloadMenu from '@/components/v2/DownloadMenu.vue';
import { CircleChevronLeft, CircleChevronRight, Info, Search, Bookmark, Plus } from '@lucide/vue';
import savedPolicyFilterService from '@/services/v2/SavedPolicyFilterService';
import type { SavedPolicyFilter, StoredFilters } from '@/types/v2/SavedPolicyFilter';
import SaveCurrentFilterModal from '@/components/v2/SaveCurrentFilterModal.vue';
import ManageSavedFiltersModal from '@/components/v2/ManageSavedFiltersModal.vue';
import TableSortMenu from '@/components/v2/TableSortMenu.vue';
import { registerResizeItem, updateResizeRegistryItem } from '@/utilities/resize';
import { WholesalerGroupPoliciesByIdParams } from '@/types/WholesalerGroup';
import { CarrierPoliciesByIdParams } from '@/types/Carrier';
import { MutationPayload } from 'vuex';
import { StoreMutationType } from '@/types/StoreMutationType';
import {
  AppliedFilter,
  FilterAccountStates,
  FilterAttachment,
  FilterCACEntity,
  FilterCarrierGroup,
  FilterClientAttributes,
  FilterCoverageType,
  FilterField,
  FilterIndustry,
  FilterMoreOptions,
  FilterRetention,
  FilterSICIndustry,
  FilterValueType,
  FilterWholesalerGroup,
  FilterDepartments,
  FilterOwnershipType,
  FilterProduct,
} from '@/types/ViewFilter.js';
import { PolicySource } from '@/types/PolicySource';

function joinAddress(addr1?: string, addr2?: string, addr3?: string, city?: string, state?: string, postalCode?: string): string {
  let addr = '';

  if (addr1) addr = `${addr1}\n`;
  if (addr2) addr = `${addr}${addr2}\n`;
  if (addr3) addr = `${addr}${addr3}\n`;
  if (city) addr = `${addr}${city}`;
  if (state) addr = `${addr}, ${state}`;
  if (postalCode) addr = `${addr} ${postalCode}`;

  return addr;
}

function formatAddress(p: EpicPolicy): EpicPolicy {
  return {
    ...p,
    obligeeAddress: joinAddress(p.obligeeAddress1, p.obligeeAddress2, p.obligeeAddress3, p.obligeeCity, p.obligeeState, p.obligeePostalCode),
    principalAddress: joinAddress(
      p.principalAddress1,
      p.principalAddress2,
      p.principalAddress3,
      p.principalCity,
      p.principalState,
      p.principalPostalCode,
    ),
    underwritingAddress: joinAddress(
      p.underwritingAddress1,
      p.underwritingAddress2,
      p.underwritingAddress3,
      p.underwritingCity,
      p.underwritingState,
      p.underwritingPostalCode,
    ),
  };
}

export default {
  name: 'PolicyDetails',
  props: {
    title: { type: String, required: false, default: 'Details' },
    scenario: {
      type: String,
      validator(value: policyDetailsType) {
        return [
          policyDetailsType.All,
          policyDetailsType.Account,
          policyDetailsType.ParentAccount,
          policyDetailsType.Carrier,
          policyDetailsType.Wholesaler,
          policyDetailsType.Program,
        ].includes(value);
      },
    },
    startDate: { type: String as PropType<string | undefined | null>, required: false },
    endDate: { type: String as PropType<string | undefined | null>, required: false },
    preloadedPolicies: { type: Array as PropType<EpicPolicy[] | undefined>, required: false },
    preloadedPoliciesLoading: { type: Boolean, default: false },
    extraColumnTitle: { type: String, required: false, default: '' },
    hideDownload: Boolean,
    showOnlyMoreFiltersSection: Boolean,
    showExpiredAtStart: Boolean,
    hideMoreFilters: Boolean,
    showFilterToggles: { type: Boolean, default: true },
    showTogglesOnTable: { type: Boolean, default: false },
    showWithoutProgramToggle: { type: Boolean, default: false },
    showWithoutProgram: { type: Boolean, default: false },
    showMoreFilters: { type: Boolean, default: false },
    searchQuery: { type: String, required: false, default: '' },
    searchField: { type: String, required: false, default: '' },
    /** Optional function (policy) => MenuItem[] for extra items at the top of each row's dropdown (same style as built-in items). */
    getExtraMenuItems: { type: Function as PropType<(policy: EpicPolicy) => unknown[]>, required: false },
    /**
     * Renders the consolidated 8-column layout (CAC-5074) with a curated sort menu instead
     * of the full 13-column, click-any-header table. Opt-in per call site rather than keyed
     * off `scenario`, since the "Select Policies" table also runs as `scenario="program"`
     * and still needs Account Name / Coverage Type to choose policies.
     */
    consolidated: { type: Boolean, default: false },
    /**
     * Prepends Coverage Type (and Account Name on a parent account) to the consolidated
     * layout, for the "Select Policies" table where those columns drive the choice rather
     * than repeat the page header.
     */
    consolidatedContext: { type: Boolean, default: false },
    /**
     * Renders inside a host that owns the URL (the Data Readiness Wizard modal). Filter state
     * stays component-local: the table neither seeds itself from the query string nor writes
     * its filters back, which would otherwise clobber the host's own query params.
     */
    embedded: { type: Boolean, default: false },
    /**
     * "Select Policies" mode. Route meta drives this on the standalone /programs/new route;
     * the prop is how an embedded host declares it without a route of its own.
     */
    createNewProgram: { type: Boolean, default: false },
  },
  data: () => ({
    searchTimeoutId: 0,
    loading: true,
    paginationResizeObserver: null as ResizeObserver | null,
    emptySrc,
    allPolicies: [] as EpicPolicy[],
    policies: [] as PolicyWithChildren[],
    currentPage: 1,
    hasPrevPage: false as boolean,
    hasNextPage: false as boolean,
    appliedFields: undefined as string[] | undefined,
    activeFilterCategory: undefined as string | undefined,
    filterOpen: false as boolean,
    appliedFilters: [] as FilterField<FilterValueType>[],
    appliedFilterBadges: [] as AppliedFilter[],
    appliedOptions: undefined as FilterMoreOptions | undefined,
    showExternalApplied: false,
    showExpiredApplied: false,
    showExternal: false,
    showExpired: false,
    showCurrentOnly: true,
    showWithoutProgramOnly: false,
    includeCoverageLines: false,
    daysUntilExpiration: null as number | null,
    extraFilters: {
      showPrimary: false,
      showExcess: false,
      showRunoff: false,
      showNoRunoff: false,
      showFavorites: false,
    },
    moreFilters: {
      showExternal: false,
      showExpired: false,
      daysUntilExpiration: null as number | null,
      showPrimary: false,
      showExcess: false,
      showRunoff: false,
      showNoRunoff: false,
      showFavorites: false,
    },
    allCoverageTypes: [] as Record<string, string>[],
    coverageTypes: [] as LabelFilterType<string>[],
    selectedCoverageTypes: [] as string[],
    appliedCoverageTypes: [] as string[],
    allSicIndustries: [] as Record<string, string>[],
    selectedSicIndustries: [] as string[],
    appliedSicIndustries: [] as string[],
    allIndustries: [] as Record<string, string>[],
    selectedIndustries: [] as string[],
    appliedIndustries: [] as string[],
    allWholesalerGroups: [] as Record<string, string>[],
    selectedWholesalerGroups: [] as string[],
    appliedWholesalerGroups: [] as string[],
    selectedCarriers: [] as string[],
    allCarriers: [] as Record<string, string>[],
    appliedCarriers: [] as string[],
    allDepartments: [] as LabelFilterType<string>[],
    appliedDepartments: [] as string[],
    selectedDepartments: [] as string[],
    allProducts: [] as LabelFilterType<string>[],
    appliedProducts: [] as string[],
    selectedProducts: [] as string[],
    selectedOwnershipTypes: [] as string[],
    appliedOwnershipTypes: [] as string[],
    selectedAttachmentRange: [] as number[],
    appliedAttachmentRange: [] as number[],
    selectedRetentionRange: [] as number[],
    appliedRetentionRange: [] as number[],
    selectedAccountAttributes: [] as string[],
    appliedAccountAttributes: [] as string[],
    allAgencies: [] as LabelValue<string>[],
    selectedAgencies: [] as string[],
    appliedAgencies: [] as string[],
    sorts: defaultPolicySorts(),
    accountAttributeTypes: [] as LabelValue<string>[],
    policyDocumentTypes: [] as DocumentType[],
    searchValue: '',
    searchIcon: Search,
    policyStats: undefined as PolicyStats | undefined,
    $route: useRoute(),
    queryParams: {} as QueryParams,
    includeIndustryChildren: false,
    listOfUSStates: [] as LabelValue<string>[],
    appliedAccountStates: [] as string[],
    selectedAccountStates: [] as string[],
    policySources: [] as PolicySource[],
    savedFilters: [] as SavedPolicyFilter[],
    showSaveFilterModal: false,
    pendingBinderReviews: [] as BinderReviewItem[],
    showUploadModal: false,
    showManageFiltersModal: false,
    consolidatedSortOptions: CONSOLIDATED_SORT_OPTIONS,
    selectedConsolidatedSort: DEFAULT_CONSOLIDATED_SORT.value,
    consolidatedSortReversed: false,
  }),
  components: {
    ExternalPolicyUploadModal,
    MenuDropdown,
    DownloadMenu,
    EmptyDisplay,
    PolicyDetailTable,
    FilterBar,
    ModernColumnFilter,
    ViewFilter,
    Toggle,
    TableSkeleton,
    TextField,
    AppButton,
    Tooltip,
    Info,
    CircleChevronLeft,
    CircleChevronRight,
    Bookmark,
    Plus,
    SaveCurrentFilterModal,
    ManageSavedFiltersModal,
    TableSortMenu,
  },
  computed: {
    policyDetailsTableHeight(): number | undefined {
      const registry = store.state.resizerRegistry;
      switch (this.scenario) {
        case policyDetailsType.All:
          return (
            window.innerHeight -
            (registry['policy-list-header']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            (registry['policy-pagination-buttons']?.height ?? 0) -
            50
          );
        case policyDetailsType.Account:
          return (
            window.innerHeight -
            (registry['account-header']?.height ?? 0) -
            (registry['account-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            40
          );
        case policyDetailsType.ParentAccount:
          return (
            window.innerHeight -
            (registry['parent-account-header']?.height ?? 0) -
            (registry['parent-account-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            40
          );
        case policyDetailsType.Carrier:
          return (
            window.innerHeight -
            (registry['carrier-header']?.height ?? 0) -
            (registry['carrier-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            88
          );
        case policyDetailsType.Wholesaler:
        case policyDetailsType.CustomWholesaler:
          return (
            window.innerHeight -
            (registry['wholesaler-header']?.height ?? 0) -
            (registry['wholesaler-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            88
          );
        default:
          return undefined;
      }
    },
    pageSize() {
      switch (this.scenario) {
        case policyDetailsType.Carrier:
        case policyDetailsType.Wholesaler:
        case policyDetailsType.CustomWholesaler:
          return 100;
        default:
          return 50;
      }
    },
    accountId() {
      return this.$route.params.id as string;
    },
    hideUnpublishedRoles() {
      return IsUserAClient(store.state.user);
    },
    isPaged() {
      return (
        this.scenario &&
        [policyDetailsType.All, policyDetailsType.Wholesaler, policyDetailsType.CustomWholesaler, policyDetailsType.Carrier].includes(
          this.scenario as policyDetailsType,
        )
      );
    },
    allowCoverageTypeFilter() {
      return [
        policyDetailsType.All,
        policyDetailsType.ParentAccount,
        policyDetailsType.Account,
        policyDetailsType.Wholesaler,
        policyDetailsType.CustomWholesaler,
      ].includes(this.scenario as policyDetailsType);
    },
    allowProductFilter() {
      return [policyDetailsType.Wholesaler, policyDetailsType.CustomWholesaler].includes(this.scenario as policyDetailsType);
    },
    allowSicIndustryFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowIndustryFilter() {
      return [policyDetailsType.All, policyDetailsType.Carrier, policyDetailsType.Wholesaler, policyDetailsType.CustomWholesaler].includes(
        this.scenario as policyDetailsType,
      );
    },
    allowWholesalerGroupFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowCarrierGroupFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAttachmentFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowRetentionFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAccountAttributesFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAgencyFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAccountStatesFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowDepartmentsFilter() {
      return this.scenario === policyDetailsType.All;
    },
    attachments() {
      return PoliciesAsHierarchy(this.allPolicies)
        .filter((p) => p.attachmentPoint)
        .map((p) => p.attachmentPoint) as number[];
    },
    filteredCoverageTypes(): string[] | undefined {
      if (Array.isArray(this.appliedCoverageTypes) && this.appliedCoverageTypes.length > 0) {
        return this.appliedCoverageTypes.filter((type): type is string => type !== undefined);
      }
      return undefined;
    },
    sicIndustries(): LabelFilterType<string>[] {
      const industries =
        this.allSicIndustries.length > 0
          ? this.allSicIndustries
          : Array.from(
              this.allPolicies
                .filter((p) => p.topLevelIndustry)
                .map((p) => p.topLevelIndustry as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return industries.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredSicIndustries(): string[] | undefined {
      if (Array.isArray(this.appliedSicIndustries) && this.appliedSicIndustries.length > 0) {
        return this.appliedSicIndustries.filter((industry): industry is string => industry !== undefined);
      }
      return undefined;
    },
    industries(): LabelFilterType<string>[] {
      const industries =
        this.allIndustries.length > 0
          ? this.allIndustries
          : Array.from(
              this.allPolicies
                .filter((p) => p.topLevelIndustry)
                .map((p) => p.topLevelIndustry as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return industries.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredIndustries(): string[] | undefined {
      if (Array.isArray(this.appliedIndustries) && this.appliedIndustries.length > 0) {
        return this.appliedIndustries.filter((industry): industry is string => industry !== undefined);
      }
      return undefined;
    },
    wholesalerGroups(): LabelFilterType<string>[] {
      const wholesalerGroups =
        this.allWholesalerGroups.length > 0
          ? this.allWholesalerGroups
          : Array.from(
              this.allPolicies
                .filter((p) => p.wholesalerGroup)
                .map((p) => p.wholesalerGroup as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return wholesalerGroups.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredWholesalerGroups(): string[] | undefined {
      if (Array.isArray(this.appliedWholesalerGroups) && this.appliedWholesalerGroups.length > 0) {
        return this.appliedWholesalerGroups.filter((wg): wg is string => wg !== undefined);
      }
      return undefined;
    },
    filteredAgencies(): string[] | undefined {
      if (Array.isArray(this.appliedAgencies) && this.appliedAgencies.length > 0) {
        return this.appliedAgencies.filter((agency): agency is string => agency !== undefined);
      }
      return undefined;
    },
    filteredAccountStates(): string[] | undefined {
      if (Array.isArray(this.appliedAccountStates) && this.appliedAccountStates.length > 0) {
        return this.appliedAccountStates.filter((state): state is string => state !== undefined);
      }
      return undefined;
    },
    filteredProducts(): string[] | undefined {
      if (Array.isArray(this.appliedProducts) && this.appliedProducts.length > 0) {
        return this.appliedProducts.filter((product): product is string => product !== undefined);
      }
      return undefined;
    },
    filteredDepartments(): string[] | undefined {
      if (Array.isArray(this.appliedDepartments) && this.appliedDepartments.length > 0) {
        return this.appliedDepartments.filter((dept): dept is string => dept !== undefined);
      }
      return undefined;
    },
    carriers(): LabelFilterType<string>[] {
      const carriers =
        this.allCarriers.length > 0
          ? this.allCarriers
          : Array.from(
              this.allPolicies
                .filter((p) => p.carrierGroup)
                .map((p) => p.carrierGroup as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return carriers.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredCarriers(): string[] | undefined {
      if (Array.isArray(this.appliedCarriers) && this.appliedCarriers.length > 0) {
        return this.appliedCarriers.filter((carrier): carrier is string => carrier !== undefined);
      }
      return undefined;
    },
    attachmentRange(): { min: number; max: number } | undefined {
      if (Array.isArray(this.appliedAttachmentRange) && this.appliedAttachmentRange.length === 2) {
        return {
          min: this.appliedAttachmentRange[0],
          max: this.appliedAttachmentRange[1],
        };
      }
      return undefined;
    },
    retentionRange(): { min: number; max: number } | undefined {
      if (Array.isArray(this.appliedRetentionRange) && this.appliedRetentionRange.length === 2) {
        return {
          min: this.appliedRetentionRange[0],
          max: this.appliedRetentionRange[1],
        };
      }
      return undefined;
    },
    filteredAccountAttributes(): string[] {
      if (Array.isArray(this.appliedAccountAttributes) && this.appliedAccountAttributes.length > 0) {
        return this.appliedAccountAttributes;
      }
      return [];
    },
    ownershipType(): string[] | undefined {
      if (this.appliedOwnershipTypes.length > 0) {
        return this.appliedOwnershipTypes;
      }
      return undefined;
    },
    runoffFlag(): string[] | undefined {
      let runoff: string[] = [];
      if (this.extraFilters.showRunoff) {
        runoff.push('1');
      }
      if (this.extraFilters.showNoRunoff) {
        runoff.push('0');
      }
      return runoff.length > 0 ? runoff : undefined;
    },
    peFlag(): string[] | undefined {
      let pe: string[] = [];
      if (this.extraFilters.showPrimary) {
        pe.push('Primary');
      }
      if (this.extraFilters.showExcess) {
        pe.push('Excess');
      }
      return pe.length > 0 ? pe : undefined;
    },
    daysUntilExpirationValue(): number | undefined {
      return this.daysUntilExpiration ? Number(this.daysUntilExpiration) : undefined;
    },
    favoritesOnly(): boolean | undefined {
      return this.extraFilters.showFavorites ? true : undefined;
    },
    showChildren() {
      return this.scenario === policyDetailsType.ParentAccount;
    },
    isCreateNewProgram() {
      return this.createNewProgram || this.$route.meta.action === 'create-new-program';
    },
    allowPolicyUpload() {
      if (this.isCreateNewProgram) {
        return false;
      }
      switch (this.scenario) {
        case policyDetailsType.Account:
        case policyDetailsType.ParentAccount:
        case policyDetailsType.Program:
          return true;
      }
      return false;
    },
    externalFilterActive() {
      for (let key in this.extraFilters) {
        if ((this.extraFilters as Record<string, boolean>)[key]) {
          return true;
        }
      }
      return false;
    },
    allFilters() {
      return this.scenario === policyDetailsType.All;
    },
    showSavedFiltersDropdown(): boolean {
      return this.scenario === policyDetailsType.All;
    },
    currentStoredFilters(): StoredFilters {
      return { appliedFilters: this.appliedFilters, appliedOptions: this.appliedOptions };
    },
    savedFilterMenuItems(): MenuItem[] {
      return this.savedFilters.map((f) => ({
        label: f.name,
        handler: () => this.applySavedFilter(f),
      }));
    },
    savedFilterActionItems(): MenuItem[] {
      return [
        {
          label: 'Save Current Filters',
          handler: () => {
            this.showSaveFilterModal = true;
          },
        },
        {
          label: 'Manage Saved Filters',
          handler: () => {
            this.showManageFiltersModal = true;
          },
          disabled: this.savedFilters.length === 0,
        },
        { label: 'Clear All Filters', handler: () => this.clearAllFilters() },
      ];
    },
    clientViewEnabled() {
      return store.state.clientViewState.isClientViewEnabled;
    },
    isUserAClient() {
      return IsUserAClient(store.state.user);
    },
    /**
     * The consolidated columns, minus AI Status for users who may not see it. Call sites that
     * pick policies rather than review them lead with the context columns — see
     * `consolidatedContextColumns`, which reads the account span off the policies themselves.
     */
    consolidatedFields() {
      const canSeeAIStatus = IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user);
      const context = this.consolidatedContext ? consolidatedContextColumns(this.allPolicies) : [];
      return [...context, ...CONSOLIDATED_POLICY_COLUMNS].filter((c) => canSeeAIStatus || c.value !== 'aiStatus');
    },
    activeConsolidatedSort() {
      const option = this.consolidatedSortOptions.find((o) => o.value === this.selectedConsolidatedSort) ?? DEFAULT_CONSOLIDATED_SORT;
      return resolveTableSort(option, this.consolidatedSortReversed);
    },
    fields() {
      if (this.consolidated) {
        return this.consolidatedFields;
      }
      const stringComparer = (field: string) => (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => {
        if (direction === 'ascending') {
          return ('' + a[field]).localeCompare('' + b[field]);
        } else {
          return ('' + b[field]).localeCompare('' + a[field]);
        }
      };
      const numberComparer = (field: string) => (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => {
        if (direction === 'ascending') {
          return ((a[field] as number) || 0) - ((b[field] as number) || 0);
        } else {
          return ((b[field] as number) || 0) - ((a[field] as number) || 0);
        }
      };
      const ppmComparer = () => (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => {
        if (direction === 'ascending') {
          return (calculatePPM(a) || -1) - (calculatePPM(b) || -1);
        } else {
          return (calculatePPM(b) || -1) - (calculatePPM(a) || -1);
        }
      };
      const amBestComparer = () => (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => {
        if (direction === 'ascending') {
          return (a.carrierRatingIndex || -1) - (b.carrierRatingIndex || -1);
        } else {
          return (b.carrierRatingIndex || -1) - (a.carrierRatingIndex || -1);
        }
      };

      const accountKey = this.scenario === policyDetailsType.ParentAccount ? 'childCompany' : 'accountName';
      type StackName = 'Policy' | 'Carrier' | 'Transaction' | 'Surety' | 'Service';

      const list: {
        stack: StackName;
        value: string;
        text: string;
        comparer?: (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => number;
      }[] = [];

      list.push({ stack: 'Policy', value: 'year', text: 'Year', comparer: numberComparer('year') });
      list.push({ stack: 'Policy', value: accountKey, text: 'Account Name', comparer: stringComparer(accountKey) });

      // Epic Account Name is only available to Admin and Broker users, on the
      // Policy Explorer, Account, and Parent Account tables.
      if (
        [policyDetailsType.All, policyDetailsType.Account, policyDetailsType.ParentAccount].includes(this.scenario as policyDetailsType) &&
        (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user))
      ) {
        list.push({
          stack: 'Policy',
          value: 'sourceAccountName',
          text: 'Epic Account Name',
          comparer: stringComparer('sourceAccountName'),
        });
      }

      if (this.scenario === policyDetailsType.All) {
        list.push({
          stack: 'Policy',
          value: 'accountAttributes',
          text: 'Client Attributes',
          comparer: stringComparer('accountAttributes'),
        });
        list.push({
          stack: 'Policy',
          value: 'parentAccountName',
          text: 'Parent Account Name',
          comparer: stringComparer('parentAccountName'),
        });
        list.push({
          stack: 'Policy',
          value: 'topLevelIndustry',
          text: 'SIC Industry',
          comparer: stringComparer('topLevelIndustry'),
        });
        list.push({
          stack: 'Policy',
          value: 'ownershipType',
          text: 'Ownership Type',
          comparer: stringComparer('ownershipType'),
        });
      }

      list.push({
        stack: 'Policy',
        value: 'policyType',
        text: 'Policy Type',
        comparer: stringComparer('policyType'),
      });
      list.push({
        stack: 'Policy',
        value: 'coverageType',
        text: 'Coverage Type',
        comparer: stringComparer('coverageType'),
      });
      list.push({
        stack: 'Policy',
        value: 'product',
        text: 'Product',
        comparer: stringComparer('product'),
      });
      list.push({
        stack: 'Policy',
        value: 'description',
        text: 'Description',
        comparer: stringComparer('description'),
      });
      list.push({
        stack: 'Policy',
        value: 'accountState',
        text: 'Client State',
        comparer: stringComparer('accountState'),
      });
      // Only show department and profit center columns to Admin and Broker users
      if (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user)) {
        list.push({
          stack: 'Transaction',
          value: 'department',
          text: 'Department',
          comparer: stringComparer('department'),
        });
        list.push({
          stack: 'Transaction',
          value: 'profitCenter',
          text: 'Profit Center',
          comparer: stringComparer('profitCenter'),
        });
      }

      // Add industry column whenever industry filter is visible
      if (this.allowIndustryFilter) {
        list.push({
          stack: 'Policy',
          value: 'genericIndustry',
          text: 'Industry',
          comparer: stringComparer('industry'),
        });
      }

      if (this.scenario !== policyDetailsType.Carrier) {
        list.push({
          stack: 'Carrier',
          value: 'carrierGroup',
          text: 'Carrier',
          comparer: stringComparer('carrierGroup'),
        });
      }

      list.push({
        stack: 'Carrier',
        value: 'issuingCompanyName',
        text: 'Issuing Company',
        comparer: stringComparer('issuingCompanyName'),
      });
      list.push({ stack: 'Carrier', value: 'carrierRating', text: 'AM Best Rating', comparer: amBestComparer() });

      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Carrier',
          value: 'syndicate',
          text: 'Syndicate Number',
          comparer: stringComparer('syndicate'),
        });
      }

      list.push({
        stack: 'Carrier',
        value: 'syndicateAgent',
        text: 'Lead Syndicate',
        comparer: stringComparer('syndicateAgent'),
      });

      list.push({
        stack: 'Carrier',
        value: 'wholesaler',
        text: 'Wholesaler',
        comparer: stringComparer('wholesaler'),
      });

      list.push({
        stack: 'Carrier',
        value: 'wholesalerGroup',
        text: 'Wholesaler Group',
        comparer: stringComparer('wholesalerGroup'),
      });

      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Carrier',
          value: 'underwriterName',
          text: 'Underwriter',
          comparer: stringComparer('underwriterName'),
        });
      }

      list.push({ stack: 'Policy', value: 'aggregateLimit', text: 'Aggregate Limit', comparer: numberComparer('aggregateLimit') });
      list.push({
        stack: 'Policy',
        value: 'occurrenceLimit',
        text: 'Occurrence Limit',
        comparer: numberComparer('occurrenceLimit'),
      });
      list.push({
        stack: 'Policy',
        value: 'attachmentPoint',
        text: 'Attachment Point',
        comparer: numberComparer('attachmentPoint'),
      });
      list.push({ stack: 'Policy', value: 'retention', text: 'Retention', comparer: numberComparer('retention') });
      list.push({ stack: 'Policy', value: 'premium', text: 'Premium', comparer: numberComparer('premium') });
      list.push({
        stack: 'Policy',
        value: 'premiumAnnualized',
        text: 'Premium (Annualized)',
        comparer: numberComparer('premiumAnnualized'),
      });
      list.push({ stack: 'Policy', value: 'ppm', text: 'PPM', comparer: ppmComparer() });
      list.push({
        stack: 'Policy',
        value: 'annualizedPPM',
        text: 'PPM (Annualized)',
        comparer: numberComparer('annualizedPPM'),
      });

      if (this.scenario !== policyDetailsType.Carrier) {
        list.push({ stack: 'Policy', value: 'ilf', text: 'ILF' });
      }

      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Transaction',
          value: 'auditAmt',
          text: 'Audit Amount',
          comparer: numberComparer('auditAmt'),
        });
      }

      list.push({
        stack: 'Policy',
        value: 'quotaShareTotal',
        text: 'Total Layer Limit',
        comparer: numberComparer('quotaShareTotal'),
      });
      list.push({ stack: 'Policy', value: 'policyID', text: 'Policy Number', comparer: stringComparer('policyID') });
      if (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user)) {
        list.push({ stack: 'Policy', value: 'aiStatus', text: 'AI Status' });
      }
      list.push({
        stack: 'Policy',
        value: 'effectiveDate',
        text: 'Effective Date',
        comparer: stringComparer('effectiveDate'),
      });
      list.push({
        stack: 'Policy',
        value: 'expirationDate',
        text: 'Expiration Date',
        comparer: stringComparer('expirationDate'),
      });
      list.push({ stack: 'Policy', value: 'sideAType', text: 'Side A', comparer: stringComparer('sideAType') });
      list.push({ stack: 'Policy', value: 'runoff', text: 'Runoff', comparer: stringComparer('runoff') });

      if (!IsUserAClient(store.state.user)) {
        list.push({ stack: 'Policy', value: 'peFlag', text: 'Layer', comparer: stringComparer('peFlag') });
        list.push({
          stack: 'Policy',
          value: 'lineStatusCode',
          text: 'Policy Status',
          comparer: stringComparer('lineStatusCode'),
        });
        list.push({
          stack: 'Transaction',
          value: 'billMode',
          text: 'Bill Mode',
          comparer: stringComparer('billMode'),
        });
        list.push({
          stack: 'Transaction',
          value: 'agencyName',
          text: 'CAC Entity',
          comparer: stringComparer('agencyName'),
        });
        list.push({
          stack: 'Transaction',
          value: 'commissionBilled',
          text: 'Commission Billed',
          comparer: numberComparer('commissionBilled'),
        });
        list.push({
          stack: 'Transaction',
          value: 'feesBilled',
          text: 'Fees Billed',
          comparer: numberComparer('feesBilled'),
        });
        list.push({
          stack: 'Transaction',
          value: 'computedRevenue',
          text: 'Revenue',
          comparer: numberComparer('computedRevenue'),
        });
        list.push({
          stack: 'Transaction',
          value: 'enteredDate',
          text: 'Entered Date',
          comparer: stringComparer('enteredDate'),
        });
        list.push({
          stack: 'Transaction',
          value: 'premiumPayableName',
          text: 'Premium Payable',
          comparer: stringComparer('premiumPayableName'),
        });
        list.push({
          stack: 'Transaction',
          value: 'rebateAmt',
          text: 'Rebate Amount',
          comparer: numberComparer('rebateAmt'),
        });
      }

      // Servicing Roles

      list.push({
        stack: 'Service',
        value: 'accountManagerRole',
        text: 'Account Manager',
        comparer: stringComparer('accountManagerRole'),
      });

      // Only show Secondary Account Manager to Admin and Broker users
      if (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user)) {
        list.push({
          stack: 'Service',
          value: 'secondaryAccountManagerRole',
          text: 'Secondary Account Manager',
          comparer: stringComparer('secondaryAccountManagerRole'),
        });
      }

      list.push({
        stack: 'Service',
        value: 'accountExecutiveRole',
        text: 'Account Executive',
        comparer: stringComparer('accountExecutiveRole'),
      });
      list.push({
        stack: 'Service',
        value: 'brokerRole',
        text: 'Broker',
        comparer: stringComparer('brokerRole'),
      });
      list.push({
        stack: 'Service',
        value: 'claimsRole',
        text: 'Claims',
        comparer: stringComparer('claimsRole'),
      });
      list.push({
        stack: 'Service',
        value: 'advisorRole',
        text: 'Advisor',
        comparer: stringComparer('advisorRole'),
      });

      // Surety

      list.push({
        stack: 'Surety',
        value: 'currTermAmount',
        text: 'Current Term Amount',
        comparer: numberComparer('currTermAmount'),
      });
      list.push({
        stack: 'Surety',
        value: 'obligeeName',
        text: 'Obligee Name',
        comparer: numberComparer('obligeeName'),
      });
      list.push({
        stack: 'Surety',
        value: 'obligeeAddress',
        text: 'Obligee Address',
        comparer: numberComparer('obligeeAddress'),
      });
      list.push({
        stack: 'Surety',
        value: 'principalName',
        text: 'Principal Name',
        comparer: numberComparer('principalName'),
      });
      list.push({
        stack: 'Surety',
        value: 'principalAddress',
        text: 'Principal Address',
        comparer: numberComparer('principalAddress'),
      });
      list.push({
        stack: 'Surety',
        value: 'underwritingName',
        text: 'Underwriting Name',
        comparer: numberComparer('underwritingName'),
      });
      list.push({
        stack: 'Surety',
        value: 'underwritingAddress',
        text: 'Underwriting Address',
        comparer: numberComparer('underwritingAddress'),
      });
      list.push({ stack: 'Surety', value: 'cancelable', text: 'Cancelable', comparer: numberComparer('cancelable') });
      list.push({ stack: 'Surety', value: 'netAmount', text: 'Net Amount', comparer: numberComparer('netAmount') });

      // Appended last: the `col` query param stores positions in this list, so inserting
      // mid-list would shift the columns of every link saved before it existed.
      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Transaction',
          value: POLICY_TABLE_COLUMNS.EXTERNAL_AGENCY_NAME,
          text: 'External Agency',
        });
      }

      return list;
    },
    filters() {
      const list: FilterField<FilterValueType>[] = [];

      if (this.allowCoverageTypeFilter) list.push({ category: 'Coverage Type', values: this.coverageTypes });
      if (this.allowSicIndustryFilter) list.push({ category: 'SIC Industry', values: this.sicIndustries });
      if (this.allowIndustryFilter) {
        list.push({
          category: 'Industry',
          values: this.industries,
          options: [
            {
              category: 'Industry',
              label: 'Include Children',
              type: 'toggle',
            },
          ],
        });
      }
      if (this.allowWholesalerGroupFilter) list.push({ category: 'Wholesaler Group', values: this.wholesalerGroups });
      if (this.allowCarrierGroupFilter) list.push({ category: 'Carrier Group', values: this.carriers });
      if (this.allowAttachmentFilter) {
        list.push({
          category: 'Attachment',
          description: 'Filter policies by attachment point',
          values: [],
          range: [0, this.staticMaxAttachmentPoint ?? 0],
          prefix: '$',
        });
      }
      if (this.allowRetentionFilter) {
        list.push({
          category: 'Retention',
          description: 'Filter policies by retention value',
          values: [],
          range: [0, this.staticMaxRetention ?? 0],
          prefix: '$',
        });
      }
      if (this.allowAccountAttributesFilter) list.push({ category: 'Client Attributes', values: this.accountAttributeTypes });
      if (this.allowAgencyFilter) list.push({ category: 'CAC Entity', values: this.allAgencies });
      if (this.allowAccountStatesFilter) list.push({ category: FilterAccountStates, values: this.listOfUSStates });
      if (this.allowDepartmentsFilter) list.push({ category: FilterDepartments, values: this.allDepartments });
      if (this.allowProductFilter) list.push({ category: FilterProduct, values: this.allProducts });
      if (this.scenario === policyDetailsType.All) list.push({ category: 'Ownership Type', values: this.ownershipTypes });

      return list;
    },
    orderedColumnValues() {
      return this.fields.map((f) => f.value);
    },
    externalSourceIDs(): number[] {
      return externalPolicySourceIDs(this.policySources);
    },
    showExternalAgencyColumn(): boolean {
      return this.showExternal && !this.consolidated && !IsUserAClient(store.state.user);
    },
    columnPickerFields() {
      return this.showExternalAgencyColumn ? this.fields : this.fields.filter((f) => f.value !== POLICY_TABLE_COLUMNS.EXTERNAL_AGENCY_NAME);
    },
    displayedFields(): string[] {
      const fields = this.appliedFields || this.defaultFields;
      return this.showExternalAgencyColumn ? fields : withExternalAgencyColumn(fields, false);
    },
    coverageTypeValues() {
      return this.coverageTypes.map((c) => c.value);
    },
    sicIndustriesValues() {
      return this.sicIndustries.map((i) => i.value);
    },
    industriesValues() {
      return this.industries.map((i) => i.value);
    },
    wholesalerGroupsValues() {
      return this.wholesalerGroups.map((w) => w.value);
    },
    carrierGroupValues() {
      return this.carriers.map((c) => c.value);
    },
    clientAttributesValues() {
      return this.accountAttributeTypes.map((a) => a.value);
    },
    agenciesValues() {
      return this.allAgencies.map((a) => a.value);
    },
    productValues() {
      return this.allProducts.map((p) => p.value);
    },
    departmentsValues() {
      return this.allDepartments.map((d) => d.value);
    },
    ownershipTypes() {
      return [
        { value: 'Public', label: 'Public' },
        { value: 'Private', label: 'Private' },
        { value: 'Not Specified', label: 'Not Specified' },
      ];
    },
    showSkeleton() {
      return this.loading || this.preloadedPoliciesLoading;
    },
    skeletonColumns() {
      const labels = this.fields.filter((f) => this.displayedFields.includes(f.value)).map((f) => f.text);
      return this.extraColumnTitle ? [this.extraColumnTitle, ...labels] : labels;
    },
    defaultFields() {
      const canSeeAIStatus = IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user);
      if (this.consolidated) {
        return this.consolidatedFields.map((c) => c.value);
      }
      const base = (() => {
        switch (this.scenario) {
          case policyDetailsType.Account:
            return [
              'coverageType',
              'description',
              'policyID',
              'aiStatus',
              'carrierGroup',
              'effectiveDate',
              'expirationDate',
              'retention',
              'attachmentPoint',
              'occurrenceLimit',
              'aggregateLimit',
              'premium',
            ];
          case policyDetailsType.Carrier:
            return [
              'accountName',
              'coverageType',
              'description',
              'policyID',
              'aiStatus',
              'effectiveDate',
              'expirationDate',
              'retention',
              'attachmentPoint',
              'occurrenceLimit',
              'aggregateLimit',
              'premium',
            ];
          case policyDetailsType.ParentAccount:
            return [
              'childCompany',
              'coverageType',
              'description',
              'policyID',
              'aiStatus',
              'carrierGroup',
              'effectiveDate',
              'expirationDate',
              'retention',
              'attachmentPoint',
              'occurrenceLimit',
              'aggregateLimit',
              'premium',
            ];
          default:
            return [
              'accountName',
              'coverageType',
              'description',
              'policyID',
              'aiStatus',
              'carrierGroup',
              'effectiveDate',
              'expirationDate',
              'retention',
              'attachmentPoint',
              'occurrenceLimit',
              'aggregateLimit',
              'premium',
            ];
        }
      })();
      const fields = canSeeAIStatus ? base : base.filter((f) => f !== 'aiStatus');
      return withExternalAgencyColumn(fields, this.showExternalAgencyColumn);
    },
    badgeCount() {
      let cnt = 0;
      for (const key in this.extraFilters) {
        if ((this.extraFilters as Record<string, boolean>)[key]) {
          cnt++;
        }
      }

      if (this.showExpired) {
        cnt++;
      }

      if ((this.daysUntilExpiration || 0) > 0) {
        cnt++;
      }

      return cnt;
    },
    retentions() {
      return PoliciesAsHierarchy(this.allPolicies)
        .filter((p) => p.retention)
        .map((p) => p.retention) as number[];
    },
    searchParams() {
      const sort = Array.isArray(this.sorts) && this.sorts.length === 1 ? this.sorts[0] : undefined;
      const params = {
        startDate: this.startDate,
        endDate: this.endDate,
        limit: this.pageSize + 1,
        offset: (this.currentPage - 1) * this.pageSize,
        parentOnly: true,
        sortField: sort?.key,
        sortDirection: sort?.direction,
        showExternal: this.showExternal,
        activeOnly: this.showCurrentOnly,
        coverageType: this.filteredCoverageTypes,
        topLevelIndustry: this.filteredSicIndustries,
        industry: this.filteredIndustries,
        includeIndustryChildren: this.includeIndustryChildren,
        wholesalerGroup: this.filteredWholesalerGroups,
        carrier: this.filteredCarriers,
        attachmentPointValueMin: this.attachmentRange?.min,
        attachmentPointValueMax: this.attachmentRange?.max,
        retentionMin: this.retentionRange?.min,
        retentionMax: this.retentionRange?.max,
        accountAttributes: this.filteredAccountAttributes,
        agency: this.filteredAgencies,
        ownershipType: this.ownershipType,
        runoffFlag: this.runoffFlag,
        peFlag: this.peFlag,
        daysUntilExpiration: this.daysUntilExpirationValue,
        favOnly: this.favoritesOnly,
        searchQuery: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchQuery,
        searchField: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchField,
        servicingRolesSearch: this.$props.searchField === 'servicingRoles' ? this.$props.searchQuery : '',
        accountStates: this.filteredAccountStates,
        departments: this.filteredDepartments,
      } as PolicyAllParams;

      return params;
    },
    wholesalerParams() {
      const sort = Array.isArray(this.sorts) ? this.sorts[0] : undefined;
      return {
        includeExpired: !this.showCurrentOnly,
        showExternal: this.showExternal,
        limit: this.pageSize + 1,
        offset: (this.currentPage - 1) * this.pageSize,
        sortField: sort?.key,
        sortDirection: sort?.direction,
        searchQuery: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchQuery,
        searchField: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchField,
        servicingRolesSearch: this.$props.searchField === 'servicingRoles' ? this.$props.searchQuery : '',
        industries: this.filteredIndustries,
        includeIndustryChildren: this.includeIndustryChildren,
        hideUnpublishedRoles: IsUserAClient(store.state.user),
        coverageTypes: this.filteredCoverageTypes,
        products: this.filteredProducts,
      } as WholesalerGroupPoliciesByIdParams;
    },
    carrierParams() {
      const sort = Array.isArray(this.sorts) ? this.sorts[0] : undefined;
      return {
        includeExpired: !this.showCurrentOnly,
        showExternal: this.showExternal,
        limit: this.pageSize + 1,
        offset: (this.currentPage - 1) * this.pageSize,
        sortField: sort?.key,
        sortDirection: sort?.direction,
        searchQuery: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchQuery,
        searchField: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchField,
        servicingRolesSearch: this.$props.searchField === 'servicingRoles' ? this.$props.searchQuery : '',
        industries: this.filteredIndustries,
        includeIndustryChildren: this.includeIndustryChildren,
        hideUnpublishedRoles: IsUserAClient(store.state.user),
      } as CarrierPoliciesByIdParams;
    },
    isParent() {
      return this.scenario === policyDetailsType.ParentAccount;
    },
    policyDetailRoute() {
      return {
        name: this.isParent ? 'parent-account-policy-detail' : 'account-policy-detail',
        params: { id: this.accountId },
      };
    },
    hasBinderScanReady() {
      return this.pendingBinderReviews.some((r) => r.status === 'ready');
    },
    // The button points at the one scan it can open. A binder still scanning has no
    // extraction to show, so there is nothing to point at and the list says why.
    pendingBinderScanToOpen() {
      return this.pendingBinderReviews.find(
        (r) => r.status === 'ready' && (r.source === 'external' ? r.submissionId : r.promptResultId) != null,
      );
    },
    // A failed scan is not in this set at all -- it has nothing to review, so it lives on
    // the list behind its own filter rather than in the queue.
    //
    // Pluralised off the same count the badge shows, so the two never disagree.
    binderScanPillLabel() {
      const plural = this.pendingBinderReviews.length === 1 ? '' : 's';
      return this.hasBinderScanReady ? `Review Binder${plural}` : `Scanning Binder${plural}`;
    },
    // A client with many policies can have hundreds of unreviewed binders; past the cap
    // the exact number stops meaning anything and only makes the button wider.
    binderScanPillCount() {
      const count = this.pendingBinderReviews.length;
      return count > 99 ? '99+' : String(count);
    },
    // Naming the file is what tells a broker it is their binder, and it is far too long
    // for the button itself. With several waiting the modal lists them instead.
    pendingBinderScanTooltip() {
      return this.pendingBinderReviews.length === 1 ? (this.pendingBinderScanToOpen?.documentName ?? '') : '';
    },
    /**
     * The consolidated table renders preloaded policies, so there is no `/policy/stats`
     * response to read totals from — count and sum what is on screen instead.
     */
    consolidatedPolicyStats(): PolicyStats {
      const rows = this.policies || [];
      return {
        totalCount: rows.length,
        totalPremium: rows.reduce((sum, p) => sum + (Number(p.premium) || 0), 0),
      };
    },
    displayedPolicyStats(): PolicyStats | undefined {
      return this.consolidated ? this.consolidatedPolicyStats : this.policyStats;
    },
    hasPolicyTotals(): boolean {
      return this.displayedPolicyStats?.totalCount !== undefined || this.displayedPolicyStats?.totalPremium !== undefined;
    },
    staticMaxRetention() {
      if (!this.policyStats) return undefined;
      return this.policyStats.maxRetention;
    },
    staticMaxAttachmentPoint() {
      if (!this.policyStats) return undefined;
      return this.policyStats.maxAttachmentPoint;
    },
    routeId() {
      return this.$route.params.id as string;
    },
    downloadMenuOptions() {
      return [
        { label: 'Download All Columns', value: 'all' },
        { label: 'Download Selected Columns', value: 'selected' },
      ];
    },
    nonChildPolicies() {
      return this.allPolicies.filter((p) => !p.isChildPolicy);
    },
    includeExpired: {
      get(): boolean {
        return !this.showCurrentOnly;
      },
      set(value: boolean) {
        this.showCurrentOnly = !value;
      },
    },
  },
  methods: {
    externalPolicyName,
    includeExternalPoliciesLabel,
    compactMoney,
    // One waiting scan needs no list -- the button opens it. Anything else, including a
    // single binder still scanning, goes to the list, which has room to say why.
    openPendingBinderScan() {
      if (this.pendingBinderReviews.length > 1 || !this.pendingBinderScanToOpen) {
        this.$router.push({ name: 'account-binder-reviews', params: { id: this.accountId } });
        return;
      }
      this.openBinderScan(this.pendingBinderScanToOpen);
    },
    // The two sources review on different screens: an external scan creates a policy,
    // an internal binder overrides the one it is already attached to.
    openBinderScan(item) {
      if (!item) {
        return;
      }
      this.$router.push(
        item.source === 'external'
          ? {
              name: 'account-external-policy-binder-review',
              params: { id: this.accountId, submissionId: String(item.submissionId) },
            }
          : {
              name: 'account-binder-review',
              params: { id: this.accountId, promptResultId: String(item.promptResultId) },
            },
      );
    },
    refreshPendingBinderScans() {
      if (this.scenario !== policyDetailsType.Account || this.isUserAClient || this.clientViewEnabled) {
        return;
      }
      binderReviewService
        .list(this.accountId, { status: PENDING_BINDER_REVIEW_STATUS })
        .then((response) => {
          this.pendingBinderReviews = response.items;
        })
        .catch(() => {
          this.pendingBinderReviews = [];
        });
    },
    handleExternalPoliciesUploaded() {
      this.$router.push({ ...this.policyDetailRoute, query: { ext: 'true' } });
    },
    observePaginationRow() {
      const el = document.getElementById('policy-pagination-buttons');
      this.paginationResizeObserver?.disconnect();
      if (!el) {
        updateResizeRegistryItem('policy-pagination-buttons', 'policy-pagination-buttons');
        return;
      }
      const observer = new ResizeObserver(() => {
        updateResizeRegistryItem('policy-pagination-buttons', 'policy-pagination-buttons');
      });
      observer.observe(el);
      this.paginationResizeObserver = markRaw(observer);
    },
    search(searchText: string) {
      if (!searchText) {
        this.filterPolicies();
        return;
      }

      const searchableFields = ['coverageType', 'description', 'carrierGroup', 'policyID', 'effectiveDate', 'expirationDate'];

      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
      }

      this.searchTimeoutId = window.setTimeout(() => {
        const filtered = this.allPolicies.filter((policy) => {
          for (const fieldName of searchableFields) {
            let fieldValue = policy[fieldName];
            if (typeof fieldValue === 'string') {
              if (['effectiveDate', 'expirationDate'].includes(fieldName)) {
                fieldValue = DateTime.fromISO(fieldValue, { zone: 'utc' }).toFormat('L/d/yy');
              }

              if ((fieldValue as string).toLowerCase().trim().includes(searchText.toLowerCase())) {
                return true;
              }
            }
          }

          return false;
        });
        this.filterPolicies(filtered);
      }, 350);
    },
    applyConsolidatedSort(value: string) {
      // Re-picking the active option reverses it; picking a different one starts from that
      // option's own default direction rather than inheriting the previous reversal.
      if (value === this.selectedConsolidatedSort) {
        this.consolidatedSortReversed = !this.consolidatedSortReversed;
      } else {
        this.selectedConsolidatedSort = value;
        this.consolidatedSortReversed = false;
      }
      trackPolicyDetailsSort(value, this.activeConsolidatedSort.direction, 'consolidated');
      this.filterPolicies();
    },
    sortByField(field: string) {
      const sort = this.sorts.find((s) => s.key === field);
      if (sort) {
        // sort field already exists. need to flip the direction
        sort.direction = sort.direction === 'ascending' ? 'descending' : 'ascending';
        this.sorts = [sort];
      } else {
        this.sorts = [{ key: field, direction: 'ascending' }];
      }
      trackPolicyDetailsSort(field, this.sorts[0].direction, 'standard');
      // not sorted by this field already
      this.currentPage = 1;
      this.refreshPolicies();
    },
    filterPolicies(source?: EpicPolicy[]) {
      this.loading = true;
      const policiesSource = source ?? this.allPolicies;
      const flatPolicies = Array.isArray(policiesSource) ? policiesSource.slice() : [];

      // Preserve expansion state from current hierarchy before replacing
      const oldState = new Map<string, { expanded?: boolean; children?: EpicPolicy[] | 'loading' }>();
      (this.policies || []).forEach((p) => {
        if (!p.isChildPolicy && p.uniqPolicy != null) {
          const expanded = (p as PolicyWithChildren).expanded;
          const children = (p as PolicyWithChildren).children;
          oldState.set(String(p.uniqPolicy), {
            expanded: typeof expanded === 'boolean' ? expanded : undefined,
            children,
          });
        }
      });

      // The consolidated table sorts here rather than through the DOM-mutating `v-tablesort`
      // directive, so Vue keeps ownership of row order. GetPoliciesAsHierarchy preserves
      // parent order, so sorting the flat list first carries through to the hierarchy.
      const orderedPolicies = this.consolidated ? sortPolicies(flatPolicies, this.activeConsolidatedSort.sorts) : flatPolicies;

      const newHierarchy = GetPoliciesAsHierarchy(orderedPolicies);
      newHierarchy.forEach((p) => {
        const old = p.uniqPolicy != null ? oldState.get(String(p.uniqPolicy)) : undefined;
        if (old) {
          const pw = p as PolicyWithChildren;
          if (old.expanded !== undefined) pw.expanded = old.expanded;
          if (old.children !== undefined) pw.children = old.children;
        }
      });
      this.policies = newHierarchy;
      this.loading = false;
    },
    async handleDownloadExport(value: string, finishExport: () => void) {
      try {
        if (value === 'all') {
          await this.downloadPolicies(false);
        } else if (value === 'selected') {
          await this.downloadPolicies(true);
        }
      } finally {
        finishExport();
      }
    },
    previousPage() {
      this.currentPage--;
      window.scrollTo(0, 0);
      if (this.isPaged) {
        this.refreshPolicies();
      } else {
        this.filterPolicies();
      }
    },
    nextPage() {
      this.currentPage++;
      window.scrollTo(0, 0);
      if (this.isPaged) {
        this.refreshPolicies();
      } else {
        this.filterPolicies();
      }
    },
    clearFilter() {
      this.moreFilters = {
        showExternal: false,
        showExpired: false,
        daysUntilExpiration: null as number | null,
        showPrimary: false,
        showExcess: false,
        showRunoff: false,
        showNoRunoff: false,
        showFavorites: false,
      };
    },
    resetFilter(category: string, filter: FilterField<FilterValueType> | undefined) {
      switch (category) {
        case FilterCoverageType:
          this.selectedCoverageTypes = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedCoverageTypes = this.selectedCoverageTypes;
          break;

        case FilterSICIndustry:
          this.selectedSicIndustries = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedSicIndustries = this.selectedSicIndustries;
          break;

        case FilterIndustry:
          this.selectedIndustries = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedIndustries = this.selectedIndustries;
          this.includeIndustryChildren = filter ? !!filter.options?.find((o) => o.label === 'Include Children') : false;
          break;

        case FilterWholesalerGroup:
          this.selectedWholesalerGroups = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedWholesalerGroups = this.selectedWholesalerGroups;
          break;

        case FilterCarrierGroup:
          this.selectedCarriers = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedCarriers = this.selectedCarriers;
          break;

        case FilterClientAttributes:
          this.selectedAccountAttributes = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedAccountAttributes = this.selectedAccountAttributes;
          break;

        case FilterRetention: {
          const range = filter?.range;
          this.selectedRetentionRange = range || [];
          this.appliedRetentionRange = this.selectedRetentionRange;
          break;
        }

        case FilterAttachment: {
          const range = filter?.range;
          this.selectedAttachmentRange = range || [];
          this.appliedAttachmentRange = this.selectedAttachmentRange;
          break;
        }

        case FilterCACEntity:
          this.selectedAgencies = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedAgencies = this.selectedAgencies;
          break;

        case FilterAccountStates: {
          this.selectedAccountStates = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedAccountStates = this.selectedAccountStates;
          break;
        }

        case FilterDepartments: {
          this.selectedDepartments = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedDepartments = this.selectedDepartments;
          break;
        }

        case FilterProduct: {
          this.selectedProducts = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedProducts = this.selectedProducts;
          break;
        }

        case FilterOwnershipType: {
          this.selectedOwnershipTypes = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedOwnershipTypes = this.selectedOwnershipTypes;
          break;
        }
      }
    },
    resetMoreOptions(moreOptions: FilterMoreOptions | undefined) {
      this.appliedFilterBadges = [];

      if (!moreOptions) {
        this.daysUntilExpiration = null;
        this.extraFilters.showRunoff = false;
        this.extraFilters.showNoRunoff = false;
        this.extraFilters.showExcess = false;
        this.extraFilters.showPrimary = false;
        this.extraFilters.showFavorites = false;

        return;
      }

      let appliedCount = 0;

      if (moreOptions.daysUntilExpiration) {
        this.daysUntilExpiration = moreOptions.daysUntilExpiration;
        appliedCount++;
      }
      if (moreOptions.runoff) {
        this.extraFilters.showRunoff = moreOptions.runoff;
        appliedCount++;
      }
      if (moreOptions.noRunoff) {
        this.extraFilters.showNoRunoff = moreOptions.noRunoff;
        appliedCount++;
      }
      if (moreOptions.excess) {
        this.extraFilters.showExcess = moreOptions.excess;
        appliedCount++;
      }
      if (moreOptions.primary) {
        this.extraFilters.showPrimary = moreOptions.primary;
        appliedCount++;
      }
      if (moreOptions.favorites) {
        this.extraFilters.showFavorites = moreOptions.favorites;
        appliedCount++;
      }

      this.appliedFilterBadges.push({
        category: 'More Options',
        count: appliedCount,
      });
    },
    async removeFilter(category: string) {
      // More Options filter removal

      if (category === FilterMoreOptions) {
        this.appliedOptions = undefined;
        this.resetMoreOptions(this.appliedOptions);
        this.clearFilter();

        const badgeIndex = this.appliedFilterBadges.findIndex((b) => b.category === category);
        if (badgeIndex > -1) {
          this.appliedFilterBadges.splice(badgeIndex, 1);
          this.appliedFilterBadges = [...this.appliedFilterBadges];
        }

        await this.refreshPolicies();
        return;
      }

      // everything else

      const index = this.appliedFilters.findIndex((f) => f.category === category);

      if (index > -1) {
        this.appliedFilters.splice(index, 1);
        this.appliedFilters = [...this.appliedFilters];

        this.resetFilter(category, undefined);

        const badgeIndex = this.appliedFilterBadges.findIndex((b) => b.category === category);
        if (badgeIndex > -1) {
          this.appliedFilterBadges.splice(badgeIndex, 1);
          this.appliedFilterBadges = [...this.appliedFilterBadges];
        }
      }

      await this.setQueryParamsFromFilters();
      this.currentPage = 1;
      this.refreshPolicies();
    },
    async activateFilter(category: string) {
      this.filterOpen = true;
      this.activeFilterCategory = category;
    },
    async applyFilters(filters: FilterField<FilterValueType>[], moreOptions?: FilterMoreOptions) {
      const filterCategories = [
        FilterCoverageType,
        FilterSICIndustry,
        FilterIndustry,
        FilterWholesalerGroup,
        FilterCarrierGroup,
        FilterAttachment,
        FilterRetention,
        FilterClientAttributes,
        FilterCACEntity,
        FilterAccountStates,
        FilterDepartments,
        FilterOwnershipType,
        FilterProduct,
      ];

      // reassign values to legacy filter refs

      filterCategories.forEach((category) => {
        const filter = filters.find((f) => f.category === category);
        this.resetFilter(category, filter);
      });

      // handle more options (legacy "More Filters" filter)

      if (
        // this if/else was ported from old "More Filters" -> Apply; not totally certain how this is used
        this.showExpiredApplied !== this.moreFilters.showExpired ||
        this.showExternalApplied !== this.moreFilters.showExternal
      ) {
        if (this.showExternalApplied !== this.moreFilters.showExternal && this.scenario === policyDetailsType.ParentAccount) {
          this.$emit('change-external', this.moreFilters.showExternal);
        }

        this.showExpiredApplied = this.moreFilters.showExpired;
        this.showExternalApplied = this.moreFilters.showExternal;

        this.showExpired = this.moreFilters.showExpired;
        this.showExternal = this.moreFilters.showExternal;
      }

      this.daysUntilExpiration = moreOptions?.daysUntilExpiration ?? null;
      this.extraFilters.showRunoff = moreOptions?.runoff ?? false;
      this.extraFilters.showNoRunoff = moreOptions?.noRunoff ?? false;
      this.extraFilters.showExcess = moreOptions?.excess ?? false;
      this.extraFilters.showPrimary = moreOptions?.primary ?? false;
      this.extraFilters.showFavorites = moreOptions?.favorites ?? false;

      this.appliedOptions = moreOptions;
      this.appliedFilters = filters;

      this.currentPage = 1;
      await this.setQueryParamsFromFilters();
      await this.refreshPolicies();
    },
    async applyFields(newFields: string[]) {
      this.appliedFields = newFields;
      await this.setQueryParamsFromFilters();
    },
    async loadSavedFilters(): Promise<void> {
      try {
        this.savedFilters = await savedPolicyFilterService.list();
      } catch {
        // non-blocking; failure is silent
      }
    },
    async applySavedFilter(filter: SavedPolicyFilter): Promise<void> {
      const stored: StoredFilters = JSON.parse(filter.filters);
      await this.applyFilters(stored.appliedFilters, stored.appliedOptions ?? undefined);
      if (filter.columns) {
        const cols: string[] = JSON.parse(filter.columns);
        await this.applyFields(cols);
      }
    },
    async clearAllFilters(): Promise<void> {
      await this.applyFilters([], undefined);
      await this.applyFields(this.defaultFields);
    },
    async onFilterSaved(): Promise<void> {
      this.showSaveFilterModal = false;
      await this.loadSavedFilters();
    },
    async expand(open: boolean, policy: PolicyWithChildren) {
      policy.expanded = !policy.expanded;
      if (!open || !policy.uniqPolicy) return;

      const needsFetch = policy.hasChildren && policy.children !== 'loading' && (!Array.isArray(policy.children) || policy.children.length === 0);
      if (needsFetch) {
        const children = await policyService.children(policy.uniqPolicy);

        this.policies.forEach((p) => {
          if (!p.isChildPolicy && p.uniqPolicy === policy.uniqPolicy) {
            p.children = children;
          }
        });
      }
    },
    applyPolicyScopeFilter() {
      if (this.preloadedPolicies) {
        this.$emit('policy-details-refresh', {
          showExternal: this.showExternal,
          showCurrentOnly: this.showCurrentOnly,
          showWithoutProgramOnly: this.showWithoutProgramOnly,
        } as PolicyDetailsFilter);
      }
      this.refreshPolicies();
    },
    async refreshPolicies(): Promise<void> {
      this.loading = true;

      // Bail if the route no longer has the id these scenarios fetch by (e.g. the user
      // navigated away while this page was still loading). Otherwise routeId reads back
      // as undefined and we request `/…/undefined/policies` (and setQueryParamsFromFilters
      // would replace the new route's query below).
      if (shouldSkipPolicyFetch(this.scenario, this.routeId)) {
        this.loading = false;
        return;
      }

      await this.setQueryParamsFromFilters();

      if (this.preloadedPolicies) {
        this.allPolicies = this.preloadedPolicies;
      } else {
        switch (this.scenario) {
          case policyDetailsType.All: {
            const policiesResponse = await policyService.all(this.searchParams);
            this.allPolicies = (policiesResponse.policies || []).map(formatAddress);
            this.policyStats = {
              ...this.policyStats,
              totalCount: policiesResponse.stats?.totalCount,
              totalPremium: policiesResponse.stats?.totalPremium,
            };

            this.setPageFromPolicies();

            break;
          }
          case policyDetailsType.Account: {
            this.allPolicies = (
              await accountService.policiesById(
                this.routeId,
                !this.showCurrentOnly,
                this.showWithoutProgramOnly,
                this.showExternal,
                this.hideUnpublishedRoles,
                this.filteredCoverageTypes,
              )
            ).map(formatAddress);

            break;
          }
          case policyDetailsType.ParentAccount: {
            this.allPolicies = (
              await parentAccountService.policiesById(
                this.routeId,
                !this.showCurrentOnly,
                !this.showExternal,
                this.hideUnpublishedRoles,
                this.filteredCoverageTypes ?? [],
              )
            ).map(formatAddress);

            break;
          }
          case policyDetailsType.Carrier: {
            this.allPolicies = (await carrierService.policiesById(this.routeId, this.carrierParams)).map(formatAddress);

            this.setPageFromPolicies();

            break;
          }
          case policyDetailsType.CustomWholesaler: {
            this.allPolicies = (await wholesalerGroupService.policiesByCustomGroup(this.routeId, this.wholesalerParams)).map(formatAddress);

            this.setPageFromPolicies();

            break;
          }
          case policyDetailsType.Program: {
            break;
          }
          default: {
            // wholesaler
            this.allPolicies = (await wholesalerGroupService.policiesById(this.routeId, this.wholesalerParams)).map(formatAddress);

            this.setPageFromPolicies();

            break;
          }
        }
      }
      this.filterPolicies();

      this.loading = false;
      await this.$nextTick();
      dispatchEvent(new Event('resize'));
    },
    setPageFromPolicies() {
      this.hasPrevPage = !!(this.currentPage - 1);
      this.hasNextPage = this.nonChildPolicies.length > this.pageSize;
      if (this.nonChildPolicies.length > this.pageSize) {
        //eslint-disable-next-line
        //@ts-ignore-next-line
        const lastNonChildPolicyIndex = (this.allPolicies as Array<EpicPolicy>).findLastIndex((policy) => !policy.isChildPolicy);
        if (lastNonChildPolicyIndex !== -1) {
          this.allPolicies.splice(lastNonChildPolicyIndex, 1);
        }
      }
    },
    buildDlpAuditConfig(metadata: Record<string, unknown>) {
      return {
        headers: {
          'X-DLP-Audit-Category': 'POLICY_EXPLORER_DOWNLOAD',
          'X-DLP-Audit-Metadata': encodeURIComponent(JSON.stringify(metadata)),
        },
      };
    },
    async downloadPolicies(displayedColumns: boolean) {
      let flattenedPolicies: EpicPolicy[];
      let parentView: boolean;
      let downloadName: string;

      const exportColumns = displayedColumns ? this.displayedFields : undefined;

      // Determine base arguments based on the scenario

      if (this.scenario === policyDetailsType.Carrier) {
        const { name } = await carrierService.findById(this.routeId);
        // Fetch all policies for download, not just the visible ones
        const allCarrierPolicies = await carrierService.policiesById(this.routeId, {
          ...this.carrierParams,
          limit: 999_999_999,
          offset: 0,
        });
        flattenedPolicies = FlattenPolicies(allCarrierPolicies.map(formatAddress)).map((p) => ({
          ...p,
          carrierGroup: name,
        }));
        parentView = false;
        downloadName = name;
      } else if (this.scenario === policyDetailsType.Wholesaler) {
        const wholesalerGroup = await wholesalerGroupService.findById(this.routeId);
        // Fetch all policies for download, not just the visible ones
        const allWholesalerPolicies = await wholesalerGroupService.policiesById(this.routeId, {
          ...this.wholesalerParams,
          limit: 999_999_999,
          offset: 0,
        });
        flattenedPolicies = FlattenPolicies(allWholesalerPolicies.map(formatAddress));
        parentView = false;
        downloadName = wholesalerGroup.name;
      } else if (this.scenario === policyDetailsType.CustomWholesaler) {
        const customWholesalerGroup = await wholesalerGroupService.customGroupById(this.routeId);
        // Fetch all policies for download, not just the visible ones
        const allCustomWholesalerPolicies = await wholesalerGroupService.policiesByCustomGroup(this.routeId, {
          ...this.wholesalerParams,
          limit: 999_999_999,
          offset: 0,
        });
        flattenedPolicies = FlattenPolicies(allCustomWholesalerPolicies.map(formatAddress));
        parentView = false;
        downloadName = customWholesalerGroup.name;
      } else if ([policyDetailsType.Account, policyDetailsType.ParentAccount].includes(this.scenario as policyDetailsType)) {
        const account = await accountService.findById(this.routeId);
        flattenedPolicies = FlattenPolicies(this.policies);
        parentView = this.scenario === policyDetailsType.ParentAccount;
        downloadName = account.accountName;
      } else if (this.scenario === policyDetailsType.Program) {
        flattenedPolicies = this.preloadedPolicies ?? [];
        parentView = this.scenario === policyDetailsType.Program;
        downloadName = 'Policies';
      } else {
        // all (Policy Explorer)
        const dlpConfig = this.buildDlpAuditConfig({
          exportFormat: 'csv',
          columns: exportColumns,
          filterCriteria: {
            dateRange: `${this.searchParams.startDate} to ${this.searchParams.endDate}`,
            coverageTypes: this.filteredCoverageTypes,
            industries: this.filteredIndustries,
            wholesalerGroups: this.filteredWholesalerGroups,
            carriers: this.filteredCarriers,
            accountStates: this.filteredAccountStates,
            departments: this.filteredDepartments,
          },
        });
        const policiesResponse = await policyService.all(
          {
            ...this.searchParams,
            parentOnly: !this.includeCoverageLines,
            limit: 999_999_999,
          },
          dlpConfig,
        );
        flattenedPolicies = policiesResponse.policies.map(formatAddress);
        parentView = this.scenario === policyDetailsType.ParentAccount;
        downloadName = 'Policies';
      }

      // filter out child policies if toggle is off (for 'all' scenario, these are already filtered out)
      if (this.scenario !== policyDetailsType.All && !this.includeCoverageLines) {
        flattenedPolicies = flattenedPolicies.filter((p) => !p.isChildPolicy);
      }

      if (displayedColumns) {
        // Consolidated column ids are display-only; a CSV wants the atomic fields behind
        // them (Effective and Expires as separate columns, etc.).
        const columns = this.consolidated ? expandConsolidatedColumns(this.appliedFields ?? []) : this.displayedFields;
        DownloadCSV(TransformPoliciesToCSV(flattenedPolicies, parentView, columns, this.externalSourceIDs), downloadName);
      } else {
        DownloadCSV(TransformPoliciesToCSV(flattenedPolicies, parentView, undefined, this.externalSourceIDs), downloadName);
      }
    },
    setFiltersFromQueryParams(): void {
      // An embedded table doesn't own the URL, so it starts from defaults rather than
      // inheriting whatever filters the host page happens to have in its query string.
      const routeQuery: LocationQuery = this.embedded ? {} : this.$route.query;
      const { col, cov, covName, prod, sic, ind, wg, cg, at, r, ca, ext, exp, due, own, run_y, run_n, pri, exs, fav, cur, ag, dept } =
        routeQuery;

      // columns filter
      if (col && typeof col === 'string') {
        this.appliedFields = col.split(',').map((i) => this.orderedColumnValues[parseInt(i)]);
      } else {
        this.appliedFields = this.defaultFields;
      }

      this.appliedFilters = [];

      // coverage types filter — covName (names) takes precedence over cov (alphabetical indices)
      if (covName && typeof covName === 'string') {
        this.appliedCoverageTypes = parseNamedFilter(covName, this.coverageTypeValues);
        this.selectedCoverageTypes = this.appliedCoverageTypes;
        if (this.appliedCoverageTypes.length > 0) {
          this.appliedFilters.push({
            category: FilterCoverageType,
            values: this.appliedCoverageTypes.map((c) => ({ value: c, label: c })),
          });
        }
      } else if (cov && typeof cov === 'string') {
        this.appliedCoverageTypes = cov.split(',').map((i) => this.coverageTypeValues[parseInt(i)]);
        this.selectedCoverageTypes = this.appliedCoverageTypes;
        this.appliedFilters.push({
          category: FilterCoverageType,
          values: this.appliedCoverageTypes.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedCoverageTypes = [];
      }

      // SIC industries filter
      if (sic && typeof sic === 'string') {
        this.appliedSicIndustries = sic.split(',').map((i) => this.sicIndustriesValues[parseInt(i)]);
        this.selectedSicIndustries = this.appliedSicIndustries;
        this.appliedFilters.push({
          category: FilterSICIndustry,
          values: this.appliedSicIndustries.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedSicIndustries = [];
      }

      // industries filter
      if (ind && typeof ind === 'string') {
        this.appliedIndustries = ind.split(',').map((i) => this.industriesValues[parseInt(i)]);
        this.selectedIndustries = this.appliedIndustries;
        this.appliedFilters.push({
          category: FilterIndustry,
          values: this.appliedIndustries.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedIndustries = [];
      }

      // wholesaler group filter
      if (wg && typeof wg === 'string') {
        this.appliedWholesalerGroups = wg.split(',').map((i) => this.wholesalerGroupsValues[parseInt(i)]);
        this.selectedWholesalerGroups = this.appliedWholesalerGroups;
        this.appliedFilters.push({
          category: FilterWholesalerGroup,
          values: this.appliedWholesalerGroups.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedWholesalerGroups = [];
      }

      // carrier groups filter
      if (cg && typeof cg === 'string') {
        this.appliedCarriers = cg.split(',').map((i) => this.carrierGroupValues[parseInt(i)]);
        this.selectedCarriers = this.appliedCarriers;
        this.appliedFilters.push({
          category: FilterCarrierGroup,
          values: this.appliedCarriers.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedCarriers = [];
      }

      // attachment point filter
      if (at && typeof at === 'string') {
        this.appliedAttachmentRange = at.split(',').map((i) => parseInt(i));
        this.selectedAttachmentRange = this.appliedAttachmentRange;
        this.appliedFilters.push({
          category: FilterAttachment,
          values: [],
          range: [this.appliedAttachmentRange[0], this.appliedAttachmentRange[1]],
        });
      } else {
        this.appliedAttachmentRange = [];
      }

      // retention filter
      if (r && typeof r === 'string') {
        this.appliedRetentionRange = r.split(',').map((i) => parseInt(i));
        this.selectedRetentionRange = this.appliedRetentionRange;
        this.appliedFilters.push({
          category: FilterRetention,
          values: [],
          range: [this.appliedRetentionRange[0], this.appliedRetentionRange[1]],
        });
      } else {
        this.appliedRetentionRange = [];
      }

      // client attributes filter
      if (ca && typeof ca === 'string') {
        this.appliedAccountAttributes = ca.split(',').map((i) => this.clientAttributesValues[parseInt(i)]);
        this.selectedAccountAttributes = this.appliedAccountAttributes;
        this.appliedFilters.push({
          category: FilterClientAttributes,
          values: this.appliedAccountAttributes.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedAccountAttributes = [];
      }

      // agency filter
      if (ag && typeof ag === 'string') {
        this.appliedAgencies = ag.split(',').map((i) => this.agenciesValues[parseInt(i)]);
        this.selectedAgencies = this.appliedAgencies;
        this.appliedFilters.push({
          category: FilterCACEntity,
          values: this.agenciesValues.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedAgencies = [];
      }

      // departments filter
      if (dept && typeof dept === 'string') {
        this.appliedDepartments = dept.split(',').map((i) => this.departmentsValues[parseInt(i)]);
        this.selectedDepartments = this.appliedDepartments;
        this.appliedFilters.push({
          category: FilterDepartments,
          values: this.appliedDepartments.map((d) => ({ value: d, label: d })),
        });
      } else {
        this.appliedDepartments = [];
      }

      // products filter — stored by name, since Product is a stable reference list
      this.appliedProducts = parseNamedFilter(prod, this.productValues);
      this.selectedProducts = this.appliedProducts;
      if (this.appliedProducts.length > 0) {
        this.appliedFilters.push({
          category: FilterProduct,
          values: this.appliedProducts.map((name) => ({ value: name, label: name })),
        });
      }

      // ownership type filter
      if (own && typeof own === 'string') {
        this.appliedOwnershipTypes = own.split(',');
        this.selectedOwnershipTypes = this.appliedOwnershipTypes;
        this.appliedFilters.push({
          category: FilterOwnershipType,
          values: this.appliedOwnershipTypes.map((ot) => ({ value: ot, label: ot })),
        });
      } else {
        this.appliedOwnershipTypes = [];
      }

      // show external policies
      if (ext) {
        this.showExternal = true;
        this.showExternalApplied = true;
      }

      // show expired policies
      if (exp) {
        this.showExpired = true;
        this.showExpiredApplied = true;
      }

      // show polcies with specified days until expiration
      if (due && typeof due === 'string') {
        const daysUntilExpiration = parseInt(due);

        if (isNaN(daysUntilExpiration)) {
          this.daysUntilExpiration = null;
        } else {
          this.daysUntilExpiration = daysUntilExpiration;

          if (!this.appliedOptions) this.appliedOptions = {};
          this.appliedOptions.daysUntilExpiration = daysUntilExpiration;
        }
      }

      // show policies WITH runoff
      if (run_y) {
        this.extraFilters.showRunoff = true;
        this.moreFilters.showRunoff = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.runoff = true;
      }

      // show policies WITHOUT runoff
      if (run_n) {
        this.extraFilters.showNoRunoff = true;
        this.moreFilters.showNoRunoff = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.noRunoff = true;
      }

      // show primary policies
      if (pri) {
        this.extraFilters.showPrimary = true;
        this.moreFilters.showPrimary = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.primary = true;
      }

      // show excess policies
      if (exs) {
        this.extraFilters.showExcess = true;
        this.moreFilters.showExcess = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.excess = true;
      }

      // show policies for favorite accounts only (Policy Explorer only — it's the one
      // scenario whose endpoint takes favOnly, and the only one offering the option)
      if (fav && this.allFilters) {
        this.extraFilters.showFavorites = true;
        this.moreFilters.showFavorites = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.favorites = true;
      }

      // show current policies only
      if (cur) {
        this.showCurrentOnly = cur === 'true';
      } else {
        this.showCurrentOnly = this.showExpiredAtStart ? false : true;
      }

      this.applyPolicyScopeFilter();
    },
    async setQueryParamsFromFilters(): Promise<void> {
      if (this.embedded) return;

      const query: QueryParams = {};

      if (this.startDate) {
        query.start = this.startDate;
      }

      if (this.endDate) {
        query.end = this.endDate;
      }

      if (this.appliedFields) {
        query.col = this.appliedFields.map((f) => this.orderedColumnValues.indexOf(f)).join(',');
      }

      if (this.appliedCoverageTypes && this.appliedCoverageTypes.length > 0) {
        query.cov = this.appliedCoverageTypes.map((ct) => ct && this.coverageTypeValues.indexOf(ct)).join(',');
      }

      if (this.appliedSicIndustries && this.appliedSicIndustries.length > 0) {
        query.sic = this.appliedSicIndustries.map((ct) => ct && this.sicIndustriesValues.indexOf(ct)).join(',');
      }
      if (this.appliedIndustries && this.appliedIndustries.length > 0) {
        query.ind = this.appliedIndustries.map((ct) => ct && this.industriesValues.indexOf(ct)).join(',');
      }

      if (this.appliedWholesalerGroups && this.appliedWholesalerGroups.length > 0) {
        query.wg = this.appliedWholesalerGroups.map((wg) => wg && this.wholesalerGroupsValues.indexOf(wg)).join(',');
      }

      if (this.appliedCarriers && this.appliedCarriers.length > 0) {
        query.cg = this.appliedCarriers.map((ct) => ct && this.carrierGroupValues.indexOf(ct)).join(',');
      }

      if (this.appliedAttachmentRange && this.appliedAttachmentRange.length > 0) {
        query.at = this.appliedAttachmentRange.join(',');
      }

      if (this.appliedRetentionRange && this.appliedRetentionRange.length > 0) {
        query.r = this.appliedRetentionRange.join(',');
      }

      if (this.appliedAccountAttributes && this.appliedAccountAttributes.length > 0) {
        query.ca = this.appliedAccountAttributes.map((a) => a && this.clientAttributesValues.indexOf(a)).join(',');
      }

      if (this.appliedAgencies && this.appliedAgencies.length > 0) {
        query.ag = this.appliedAgencies.map((a) => a && this.agenciesValues.indexOf(a)).join(',');
      }

      if (this.appliedProducts && this.appliedProducts.length > 0) {
        query.prod = this.appliedProducts.join(',');
      }

      if (this.appliedDepartments && this.appliedDepartments.length > 0) {
        query.dept = this.appliedDepartments.map((d) => d && this.departmentsValues.indexOf(d)).join(',');
      }

      if (this.appliedOwnershipTypes && this.appliedOwnershipTypes.length > 0) {
        query.own = this.appliedOwnershipTypes.join(',');
      }

      if (this.showExternal) {
        query.ext = 'true';
      }

      if (this.showExpired) {
        query.exp = 'true';
      }

      if (this.daysUntilExpiration && this.daysUntilExpiration > 0) {
        query.due = this.daysUntilExpiration.toString();
      }

      if (this.extraFilters.showRunoff) {
        query.run_y = 'true';
      }

      if (this.extraFilters.showNoRunoff) {
        query.run_n = 'true';
      }

      if (this.extraFilters.showPrimary) {
        query.pri = 'true';
      }

      if (this.extraFilters.showExcess) {
        query.exs = 'true';
      }

      if (this.allFilters && this.extraFilters.showFavorites) {
        query.fav = 'true';
      }

      query.cur = this.showCurrentOnly ? 'true' : 'false';

      if (Object.keys(query).length) {
        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          await this.$router.replace({ query } as RouteLocationRaw).catch(() => {
            return '';
          });
        }
      }
    },
    recomputeFilterBadges() {
      this.appliedFilterBadges = this.appliedFilters.map((f) => {
        let count = 0;

        switch (f.category) {
          case FilterRetention:
          case FilterAttachment:
            if (f.range) count = 1;
            break;
          default:
            count = f.values.length;
        }

        return { category: f.category, count };
      });

      if (this.appliedOptions) {
        let appliedCount = 0;

        if (this.appliedOptions.daysUntilExpiration) appliedCount++;
        if (this.appliedOptions.runoff) appliedCount++;
        if (this.appliedOptions.noRunoff) appliedCount++;
        if (this.appliedOptions.excess) appliedCount++;
        if (this.appliedOptions.primary) appliedCount++;
        if (this.appliedOptions.favorites) appliedCount++;

        if (appliedCount > 0) {
          this.appliedFilterBadges.push({ category: FilterMoreOptions, count: appliedCount });
        }
      }
    },
  },
  watch: {
    preloadedPolicies() {
      this.allPolicies = this.preloadedPolicies || [];
      this.filterPolicies();
    },
    /**
     * The consolidated context columns are read off the policies, which arrive after the
     * columns are first applied — follow the new defaults, but leave a curated column set alone.
     */
    defaultFields(next: string[], previous: string[]) {
      if (!this.consolidatedContext) return;
      const untouched =
        !this.appliedFields || (this.appliedFields.length === previous.length && this.appliedFields.every((f, i) => f === previous[i]));
      if (untouched) this.appliedFields = next;
    },
    startDate() {
      this.currentPage = 1;
      this.refreshPolicies();
    },
    endDate() {
      this.currentPage = 1;
      this.refreshPolicies();
    },
    showExpired(show: boolean) {
      this.currentPage = 1;
      this.showCurrentOnly = !show;
    },
    showCurrentOnly() {
      this.currentPage = 1;
      this.applyPolicyScopeFilter();
    },
    showExternal() {
      if (this.appliedFields && !this.consolidated) {
        this.appliedFields = withExternalAgencyColumn(this.appliedFields, this.showExternalAgencyColumn);
      }
      if (!this.showExternalAgencyColumn) {
        this.sorts = sortsWithoutExternalAgency(this.sorts);
      }
      this.currentPage = 1;
      this.applyPolicyScopeFilter();
    },
    showWithoutProgramOnly() {
      this.currentPage = 1;
      this.applyPolicyScopeFilter();
    },
    clientViewEnabled() {
      this.appliedFields = this.defaultFields;
    },
    searchQuery() {
      this.currentPage = 1;
      this.refreshPolicies();
    },
    searchField() {
      if (this.searchQuery?.length > 0) {
        this.currentPage = 1;
        this.refreshPolicies();
      }
    },
    appliedFilters() {
      this.recomputeFilterBadges();
    },
    appliedOptions() {
      this.recomputeFilterBadges();
    },
    async loading(isLoading: boolean) {
      if (!isLoading) {
        await this.$nextTick();
        this.observePaginationRow();
      }
    },
  },
  mounted() {
    registerResizeItem({ name: 'policy-details-subheader', selector: 'policy-details-subheader' });

    this.refreshPendingBinderScans();
    registerResizeItem({ name: 'policy-pagination-buttons', selector: 'policy-pagination-buttons' });

    store.subscribe((mutation: MutationPayload) => {
      if (mutation.type === StoreMutationType.ENABLE_CLIENT_VIEW_MODE || mutation.type === StoreMutationType.DISABLE_CLIENT_VIEW_MODE) {
        this.appliedFields = this.defaultFields;
      }
    });
  },
  beforeUnmount() {
    this.paginationResizeObserver?.disconnect();
    this.paginationResizeObserver = null;
  },
  async beforeMount() {
    if (this.scenario === policyDetailsType.Account || this.scenario === policyDetailsType.ParentAccount) {
      // if non-prospect account has no active internal policies, automatically toggle on external policies
      const fetchKPI =
        this.scenario === policyDetailsType.ParentAccount
          ? () => accountService.kpiByParentId(this.routeId, false, true)
          : () => accountService.kpi(this.routeId);

      const [accountInfo, coverageTypes, kpi] = await Promise.all([
        accountService.findById(this.routeId),
        accountServiceV2.getCoverageTypes(Number(this.routeId)),
        fetchKPI(),
      ]);

      this.coverageTypes = coverageTypes?.map((ct) => ({ value: ct, label: ct })).sort((a, b) => a.label.localeCompare(b.label));

      if (accountInfo.accountStatus === 'P') {
        this.showCurrentOnly = false;
        this.showExternal = true;
      } else if ((kpi.currentPolicies ?? 0) === 0) {
        this.showExternal = true;
      }
    }

    if (this.scenario === policyDetailsType.All && !this.preloadedPolicies) {
      const [
        coverageTypes,
        topLevelIndustries,
        wholesalerGroups,
        carriers,
        accountAttributes,
        policyDocumentTypes,
        agencyTypes,
        departments,
        policyStats,
      ] = await Promise.all([
        referenceService.coverageTypes(),
        referenceService.topLevelIndustries(),
        wholesalerGroupService.list(),
        referenceService.carrierGroups(),
        referenceService.accountAttributeTypes(),
        documentService.getDocumentTypes(),
        referenceService.agencyTypes(),
        referenceService.departments(),
        policyService.stats({ startDate: this.searchParams.startDate, endDate: this.searchParams.endDate }),
      ]);

      this.policyStats = policyStats;

      this.allCoverageTypes = coverageTypes
        .map((c) => c.label)
        .map((c) => ({ text: c, value: c }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.coverageTypes = this.allCoverageTypes.map((ct) => ({ value: ct.value, label: ct.text }));

      this.allSicIndustries = topLevelIndustries
        .map((i) => i.Name)
        .map((i) => ({ text: i, value: i }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.allWholesalerGroups = wholesalerGroups
        .map((w) => ({ text: w.name.trim(), value: w.name.trim() }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.allCarriers = carriers
        .map((c) => c.label)
        .map((c) => ({ text: c, value: c }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.accountAttributeTypes = accountAttributes.map((a) => a.name).map((a) => ({ label: a, value: a }));
      this.policyDocumentTypes = policyDocumentTypes.filter((t) => t.domainName === 'Policy');
      this.allAgencies = agencyTypes.map((a) => ({ label: a.agencyName, value: a.agencyName }));
      this.allDepartments = departments.map((d) => ({ value: d.label, label: d.label })).sort((a, b) => a.label.localeCompare(b.label));
      this.listOfUSStates = USStates;
    } else {
      this.policyDocumentTypes = (await documentService.getDocumentTypes()).filter((t) => t.domainName === 'Policy');
    }

    if (this.scenario === policyDetailsType.Wholesaler || this.scenario === policyDetailsType.CustomWholesaler) {
      const [coverageTypes, products] = await Promise.all([referenceService.coverageTypes(), referenceService.products()]);

      this.coverageTypes = coverageTypes.map((c) => ({ value: c.label, label: c.label })).sort((a, b) => a.label.localeCompare(b.label));
      this.allProducts = products.map((p) => ({ value: p.name, label: p.name })).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (this.allowIndustryFilter) {
      const industries = await referenceService.industries();
      this.allIndustries = industries
        .map((i) => i.name)
        .map((i) => ({ text: i, value: i }))
        .sort((a, b) => a.text.localeCompare(b.text));
    }

    this.policySources = await policySourceService.list();

    this.setFiltersFromQueryParams();

    await this.loadSavedFilters();

    return;
  },
};
</script>

<style scoped>
.wrapper {
  border-bottom: 1px solid #cccccc;
  margin: 0 1em;
}

.wrapper:last-child {
  border-bottom: none;
}
</style>
